import { FC, ReactNode, useMemo, useEffect, lazy } from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector } from '~/store/types';
import { AccountAutoWithdrawalsPopupContainer } from '~/components/payment/async';
import { authenticationSelector, showWelcomePopupSelector } from '~/components/authentication/ducks/selectors';
import { ProductSwitch, PlayerInfoScreen } from '~/components/account/async';
import { selectPanicReason } from '~/components/account/reality-check/ducks/selectors';
import { selectIsCasinoAndSportsBookProduct, selectAutoWithdraw } from '~/components/account/ducks/index';
import { selectIsPopupOpen } from '~/components/account/product-switch/product-switch-duck';
import PanicReason from '../pages/panic-reason';
import {
	selectGermanProductSwitchEnabled,
	selectIsGermanRegulatedFranchise,
	selectTempAccountFlow
} from '../brand/ducks/selectors';
import UIContext from '../../ui/ui-context';
import useUIContext from '~/ui/use-ui-context';
import Blur from '../misc/blur';
import ClosurePopup from '~/components/account/closure-popup';
import MarketingConsentPopup from '~/components/account/marketing-consent-popup';
import { sendAppBridgeEvent, WEB_EVENTS } from '~/components/app-bridge';
import { isNativeProductSwitchEnabled } from '~/components/app-bridge/app-properties';
import HostnameChangeReload from '../hostname-change-reload';
import WelcomeScreenPopUpDEGA from '~/components/account/welcome/welcome-screen-popup-dega';

const TempAccLogout = lazy(() => import('../authentication/temporary-account/temp-account-logout'));

interface RegulationsContainerDEGAProps {
	children: ReactNode;
}

const RegulationsContainerDEGA: FC<RegulationsContainerDEGAProps> = ({ children }) => {
	const isCasinoAndSportsBookProduct = useAppSelector(selectIsCasinoAndSportsBookProduct);

	const panicReasonActive = !!useSelector(selectPanicReason);
	const isProductSwitchPopupOpen = useSelector(selectIsPopupOpen);
	const isGermanProductSwitchEnabled = useSelector(selectGermanProductSwitchEnabled);
	const isGermanRegulatedFranchise = useSelector(selectIsGermanRegulatedFranchise);
	const authenticated = useSelector(authenticationSelector);
	const autoWithdraw = useSelector(selectAutoWithdraw);
	const showTempAccountFlow = useSelector(selectTempAccountFlow);
	const shouldShowWelcomePopup = useSelector(showWelcomePopupSelector);

	const showAccountAutoWithdrawalsPopup = authenticated && autoWithdraw?.hasAutoWithdrawWin;

	// Disable product-switch if mobile apps enabled native counterpart
	const isProductSwitchEnabled =
		isGermanProductSwitchEnabled && isCasinoAndSportsBookProduct && !isNativeProductSwitchEnabled();

	const uiContext = useUIContext();
	const newUiContext = useMemo(
		() => ({
			...uiContext,
			hasSidebar: !panicReasonActive
		}),
		[uiContext, panicReasonActive]
	);
	useEffect(() => {
		sendAppBridgeEvent(WEB_EVENTS.DEGA_AUTOWITHDRAW_POPUP, [{ autoWithdraw }]);
	}, [showAccountAutoWithdrawalsPopup, autoWithdraw]);

	return (
		<>
			<PanicReason />
			{/* TODO: Is this needed */}
			<HostnameChangeReload />
			{isProductSwitchEnabled && <ProductSwitch />}
			{authenticated && <MarketingConsentPopup />}
			{showTempAccountFlow && <TempAccLogout />}
			{shouldShowWelcomePopup && <WelcomeScreenPopUpDEGA />}
			<ClosurePopup />
			<UIContext.Provider value={newUiContext}>
				<Blur enable={panicReasonActive || isProductSwitchPopupOpen}>
					{showAccountAutoWithdrawalsPopup && (
						<AccountAutoWithdrawalsPopupContainer autoWithdraw={autoWithdraw} />
					)}
					{authenticated && isGermanRegulatedFranchise && <PlayerInfoScreen />}
					{children}
				</Blur>
			</UIContext.Provider>
		</>
	);
};

export default RegulationsContainerDEGA;
